import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, Check, AlertCircle, ChevronLeft } from 'lucide-react';
import { serverApi, packageApi } from '../../services/api';
import { useTranslation } from 'react-i18next';
import type { GameFieldConfig } from '../../types/package';
import { getApiErrorDetails } from '../../utils/apiError';

interface Package {
  _id: string;
  name: string;
  displayName: string;
  description: string;
  game: string;
  price: number;
  ram: number;
  cpu: number;
  storage: number;
  maxPlayers: number;
  isFree: boolean;
}

const CREATE_SERVER_ERROR_I18N_KEYS: Record<string, string> = {
  no_agent_cpu_capacity_for_package: 'createServer.noCpuCapacity',
  no_agent_memory_capacity_for_package: 'createServer.noMemoryCapacity',
  no_agent_storage_capacity_for_package: 'createServer.noStorageCapacity',
  no_agent_weight_capacity_for_package: 'createServer.noWeightCapacity',
  no_agent_package_count_capacity_for_package: 'createServer.noPackageCapacity',
  no_agent_ports_for_package: 'createServer.noPortCapacity',
  no_agent_capacity_or_ports_for_package: 'createServer.noGenericCapacity',
};

interface GameOption {
  id: string;
  name: string;
  image: string;
}

const GAME_OPTIONS: GameOption[] = [
  { id: 'minecraft', name: 'Minecraft', image: 'https://cdn-icons-png.flaticon.com/512/5969/5969059.png' },
  { id: 'valheim', name: 'Valheim', image: 'https://cdn2.steamgriddb.com/icon/0a09c8844ba8f527a1f39b7c2509658c/32/256x256.png' },
];

interface CreateServerModalProps {
  packages: Package[];
  onClose: () => void;
  onSuccess: (serverId: string) => void;
}

type Step = 'game' | 'details';

export function CreateServerModal({ packages, onClose, onSuccess }: CreateServerModalProps) {
  const [step, setStep] = useState<Step>('game');
  const [selectedGame, setSelectedGame] = useState<string>('');
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [serverName, setServerName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [fieldConfigs, setFieldConfigs] = useState<GameFieldConfig[]>([]);
  const [gameSettings, setGameSettings] = useState<Record<string, any>>({});
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [fieldsError, setFieldsError] = useState('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [failedGameImages, setFailedGameImages] = useState<Set<string>>(new Set());
  const { t } = useTranslation();

  const filteredPackages = packages.filter((p) => p.game === selectedGame);

  const createServerMutation = useMutation({
    mutationFn: (data: any) => serverApi.createServer(data),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    
    if (!selectedPackage) {
      setErrorMsg(t('createServer.selectPackageError'));
      return;
    }
    if (fieldsLoading) {
      setErrorMsg(t('createServer.settingsLoading'));
      return;
    }
    if (fieldsError) {
      setErrorMsg(fieldsError);
      return;
    }
    if (!serverName.match(/^[a-z0-9-]+$/)) {
      setErrorMsg(t('createServer.subdomainError'));
      return;
    }

    try {
      const submittedSettings = { ...gameSettings };
      for (const field of fieldConfigs) {
        if (field.sensitive && submittedSettings[field.name] === '') {
          delete submittedSettings[field.name];
        }
      }
      const response = await createServerMutation.mutateAsync({
        name: serverName.trim(),
        displayName: displayName.trim(),
        packageId: selectedPackage._id,
        gameSettings: submittedSettings,
      });
      const createdServerId = response?.result?.serverId;
      if (!response?.success || typeof createdServerId !== 'string') {
        throw new Error(t('createServer.invalidCreateResponse'));
      }
      onSuccess(createdServerId);
    } catch (error: any) {
      const details = getApiErrorDetails(error, t('createServer.createFailed'));
      const code = details.code;
      if (code === 'subdomain_taken') setErrorMsg(t('createServer.subdomainTaken'));
      else if (code === 'invalid_subdomain_format') setErrorMsg(t('createServer.invalidSubdomain'));
      else if (code && CREATE_SERVER_ERROR_I18N_KEYS[code]) {
        setErrorMsg(t(CREATE_SERVER_ERROR_I18N_KEYS[code]));
      }
      else setErrorMsg(details.message);
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function fetchFields() {
      if (!selectedPackage) {
        setFieldConfigs([]);
        setGameSettings({});
        setFieldsError('');
        setFieldsLoading(false);
        return;
      }
      setFieldsLoading(true);
      setFieldsError('');
      try {
        const resp = await packageApi.getPackageFieldConfigs(selectedPackage._id);
        if (cancelled) return;
        const fields = resp.fieldConfigs || [];
        setFieldConfigs(fields);
        const defaults: Record<string, any> = {};
        fields.forEach((f) => {
          const value = f.defaultValue !== undefined
            ? f.defaultValue
            : (f.type === 'boolean' ? false : undefined);
          if (value !== undefined) defaults[f.name] = value;
        });
        setGameSettings(defaults);
      } catch (error) {
        if (cancelled) return;
        setFieldConfigs([]);
        setGameSettings({});
        setFieldsError(getApiErrorDetails(error, t('createServer.settingsLoadError')).message);
      } finally {
        if (!cancelled) setFieldsLoading(false);
      }
    }
    fetchFields();
    return () => { cancelled = true; };
  }, [selectedPackage]);

  function renderField(f: GameFieldConfig) {
    const value = gameSettings[f.name] ?? (f.type === 'boolean' ? false : '');
    const onChange = (val: any) => setGameSettings((prev) => ({ ...prev, [f.name]: val }));
    
    const effectiveMax = f.max;
    
    if (f.type === 'string') {
      return (
        <div key={f.name}>
          <label className="text-sm text-gray-300 mb-1 block">{f.label}</label>
          <input
            type={f.sensitive ? 'password' : 'text'}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            minLength={f.minLength}
            maxLength={f.maxLength}
            className="input"
            placeholder={f.sensitive && f.hasDefault
              ? t('createServer.sensitiveDefaultPlaceholder')
              : f.description}
            required={!!f.required && !f.hasDefault}
          />
          {f.sensitive && f.hasDefault && (
            <p className="text-xs text-gray-500 mt-1">
              {t('createServer.sensitiveDefaultHelp')}
            </p>
          )}
        </div>
      );
    }
    if (f.type === 'number') {
      return (
        <div key={f.name}>
          <label className="text-sm text-gray-300 mb-1 block">{f.label}</label>
          <input
            type="number"
            value={value}
            min={f.min}
            max={effectiveMax}
            onChange={(e) => {
              const num = Number(e.target.value);
              if (effectiveMax !== undefined && num > effectiveMax) return onChange(effectiveMax);
              onChange(num);
            }}
            className="input"
            required={!!f.required}
          />
          {effectiveMax !== undefined && (
            <p className="text-xs text-gray-500 mt-1">Max: {effectiveMax}</p>
          )}
        </div>
      );
    }
    if (f.type === 'boolean') {
      return (
        <div key={f.name} className="flex items-center gap-2">
          <input
            id={f.name}
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="checkbox"
          />
          <label htmlFor={f.name} className="text-sm text-gray-300">{f.label}</label>
        </div>
      );
    }
    return (
      <div key={f.name}>
        <label className="text-sm text-gray-300 mb-1 block">{f.label}</label>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="input"
          required={!!f.required}
        >
          {(f.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-4xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-dark-600">
          <div className="flex items-center gap-3">
            {step === 'details' && (
              <button
                onClick={() => { setStep('game'); setSelectedPackage(null); setErrorMsg(''); }}
                className="btn-ghost p-1.5"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <h2 className="text-xl font-bold text-gray-100">{t('createServer.title')}</h2>
          </div>
          <button onClick={onClose} className="btn-ghost p-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step 1: Game Selection */}
        {step === 'game' && (
          <div className="p-6">
            <h3 className="section-title mb-2">{t('createServerGame.chooseGame')}</h3>
            <p className="text-sm text-gray-500 mb-6">{t('createServerGame.chooseGameSubtitle')}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {GAME_OPTIONS.map((game) => (
                <button
                  key={game.id}
                  onClick={() => {
                    setSelectedGame(game.id);
                    setSelectedPackage(null);
                    setStep('details');
                  }}
                  aria-label={game.name}
                  className="card p-6 hover:border-neon-500/40 transition-all group text-center"
                >
                  <div className="w-20 h-20 mx-auto mb-4 bg-dark-700 rounded-xl flex items-center justify-center overflow-hidden group-hover:bg-dark-600 transition-colors">
                    {failedGameImages.has(game.id) ? (
                      <span className="text-2xl font-bold text-neon-400" aria-hidden="true">
                        {game.name[0]}
                      </span>
                    ) : (
                      <img
                        src={game.image}
                        alt=""
                        className="w-12 h-12 object-contain"
                        onError={() => {
                          setFailedGameImages((current) => new Set(current).add(game.id));
                        }}
                      />
                    )}
                  </div>
                  <h4 className="text-lg font-semibold text-gray-100 group-hover:text-neon-400 transition-colors">{game.name}</h4>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Server Details */}
        {step === 'details' && (
        <form onSubmit={handleSubmit} className="p-6">
          {/* Server Details */}
          <div className="mb-8">
            <h3 className="section-title mb-4">{t('createServer.serverDetails')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-300 mb-1 block">{t('createServer.subdomain')}</label>
                <input
                  type="text"
                  value={serverName}
                  onChange={(e) => setServerName(e.target.value.toLowerCase())}
                  className="input"
                  placeholder="my-server"
                  pattern="^[a-z0-9-]+$"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {serverName || 'your-server'}.hostinator.ro
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1 block">{t('createServer.displayName')}</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="input"
                  placeholder={t('createServer.displayNamePlaceholder')}
                  required
                />
              </div>
            </div>
          </div>

          {/* Package Selection */}
          <div className="mb-8">
            <h3 className="section-title mb-4">{t('createServer.selectPackage')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {filteredPackages.map((pkg) => (
                <div
                  key={pkg._id}
                  onClick={() => setSelectedPackage(pkg)}
                  className={`card p-4 cursor-pointer transition-all ${
                    selectedPackage?._id === pkg._id
                      ? 'border-neon-500/50 bg-neon-500/5'
                      : 'hover:border-dark-500'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-200 text-sm">{pkg.displayName}</h4>
                    {selectedPackage?._id === pkg._id && (
                      <Check className="w-4 h-4 text-neon-400" />
                    )}
                  </div>
                  
                  <div className="text-lg font-bold text-neon-400 mb-2">
                    {pkg.price === 0 ? t('common.free') : `${pkg.price} RON`}
                  </div>
                  
                  <div className="text-xs text-gray-500 space-y-0.5">
                    <div>{pkg.ram}GB RAM</div>
                    <div>{pkg.cpu} vCPU</div>
                    <div>{pkg.storage}GB storage</div>
                    <div>{pkg.maxPlayers} players</div>
                  </div>
                </div>
              ))}
            </div>
            {filteredPackages.length === 0 && (
              <div className="mt-4 p-4 rounded-lg border border-amber-500/20 bg-amber-500/10 text-sm text-amber-300">
                {t('createServer.noPackagesForGame')}
              </div>
            )}
          </div>

          {/* Game Settings */}
          {selectedPackage && fieldConfigs.length > 0 && (
            <div className="mb-8">
              <h3 className="section-title mb-4">{t('createServer.settings')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {fieldConfigs.map(renderField)}
              </div>
            </div>
          )}

          {selectedPackage && fieldsError && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
              <div>
                <p className="text-red-300 text-sm font-medium">{t('createServer.settingsLoadError')}</p>
                <p className="text-red-400 text-xs mt-1">{fieldsError}</p>
              </div>
            </div>
          )}

          {/* Error */}
          {errorMsg && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
              <p className="text-red-400 text-sm">{errorMsg}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-dark-600">
            <button type="button" onClick={onClose} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!selectedPackage || !serverName.trim() || !displayName.trim() || fieldsLoading || !!fieldsError || createServerMutation.isPending}
              className="btn-primary"
            >
              {createServerMutation.isPending ? (
                <><div className="spinner w-4 h-4" /> {t('createServer.creating')}</>
              ) : (
                t('createServer.createServer')
              )}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
